import PropTypes from 'prop-types';
import { Box, Typography, Paper } from '@mui/material';
import Grid from '@mui/material/Grid2';

import logger from 'utils/logger';

const log = logger.createLogger('AttachmentViewer');

const AttachmentViewer = ({ attachmentData, selectedAttachment }) => {
  // Check if we have data to display
  if (!attachmentData) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
        }}>
        <Typography
          variant="h6"
          sx={{
            color: 'error.main',
            textAlign: 'center',
            padding: 4,
          }}>
          No data available for this attachment
        </Typography>
      </Box>
    );
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (error) {
      log.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Format any value for display
  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        log.error('Error formatting object:', e);
        return 'Error formatting object';
      }
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return value.toString();
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
      }}>
      {/* Attachment Information Section */}
      <Paper
        sx={{
          bgcolor: 'background.paper',
          p: 2,
          borderRadius: 1,
          mb: 2,
        }}
        elevation={1}>
        <Grid container spacing={2}>
          <Grid size={6}>
            <Typography variant="body2">
              <strong>Attachment ID:</strong>{' '}
              {selectedAttachment.attachmentId || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>File Name:</strong> {selectedAttachment.filename || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>File Type:</strong> {selectedAttachment.filetype || 'N/A'}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Created At:</strong>{' '}
              {formatDate(selectedAttachment.createdAt)}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>File Path:</strong> {selectedAttachment.filepath || 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Complete Attachment Data Section */}
      <Typography
        variant="h6"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 1,
          mb: 2,
        }}>
        Full parsed text
      </Typography>
      <Paper
        elevation={0}
        sx={{
          bgcolor: '#f5f5f5',
          p: 2,
          borderRadius: '4px',
          fontSize: '0.9rem',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflow: 'auto',
          maxHeight: '400px',
          minHeight: '200px',
        }}>
        {formatValue(attachmentData)}
      </Paper>
    </Box>
  );
};

AttachmentViewer.propTypes = {
  attachmentData: PropTypes.object,
  selectedAttachment: PropTypes.object,
};

export default AttachmentViewer;
